import React, { useState, useContext, useEffect, useRef } from 'react';
import { View, StyleSheet, Text, Dimensions, ScrollView, Platform, useColorScheme } from "react-native";
import axios from 'axios';
import { useAuthContext } from '../../../hooks/useAuthContext';
import { showMessage } from "react-native-flash-message";
import { useTheme } from '@react-navigation/native';

import CustomInput from '../../../components/CustomInput';
import CustomButton from '../../../components/CustomButton';
import CustomText from '../../../components/CustomText/CustomText';

// Screen and window dimensions 
const window = Dimensions.get("window");
const screen = Dimensions.get("screen");

// * Handle SVG logo 
import SvgXml from 'react-native-svg';

// * Logo Path
import { Light, Dark } from '../../../img/logos';

// * Language 
import { useTranslation } from 'react-i18next';


const UpdatePasswordScreen = ({ navigation, route }) => {

  const { t } = useTranslation();

  let colorScheme = useColorScheme();
  colorScheme = 'light'
  const { colors } = useTheme();

  const styles = makeStyles(colors, colorScheme)
  const { username, user, clientApi, dispatch } = useAuthContext();

  const currentPasswordRef = useRef(null);
  const newPasswordRef = useRef(null);
  const confirmPasswordRef = useRef(null);
  const submitClickedRef = useRef(null);
  const { isLoading, setIsLoading } = useState(false);

  const [currentPassword, setCurrentPassword] = useState("")
  const [newPassword, setNewPassowrd] = useState("");
  const [confirmPassowrd, setConfirmPassword] = useState("");
  const [submitClicked, setSubmitClicked] = useState(false);
  const [dimensions, setDimensions] = useState({ window, screen });

  // validation errors
  const [currentPasswordError, setCurrentPasswordError] = useState("");
  const [newPasswordError, setNewPassowrdError] = useState("");
  const [confirmPassowrdError, setConfirmPasswordError] = useState("");


  useEffect(() => {
    currentPasswordRef.current = currentPassword;
    if (submitClickedRef.current) {
      validateCurrentPassword();
    };
  }, [currentPassword]);

  useEffect(() => {
    newPasswordRef.current = newPassword;
    if (submitClickedRef.current) {
      validateNewPassword();
    };
  }, [newPassword]);

  useEffect(() => {
    confirmPasswordRef.current = confirmPassowrd;
    if (submitClickedRef.current) {
      validateConfirmPassword();
    };
  }, [confirmPassowrd]);

  useEffect(() => {
    submitClickedRef.current = submitClicked;
  }, [submitClicked]);

  const onSavePassword = async (e) => {
    e.preventDefault();
    if (!validateConfirmPassword() || !validateCurrentPassword() || !validateNewPassword())
      return

    setSubmitClicked(true);
    let passwordData = {
      Username: username,
      name: username,
      emplId: username,
      currentPassword: currentPasswordRef.current,
      newPassword: newPasswordRef.current,
    }
    await axios.post(`${clientApi}api/Employee/savenewpassword`, passwordData, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + user.token
      },
    })
      .then((response) => {
        if (response.status === 200) {
          // update the auth context
          showMessage({
            message: t('common:detailsSaved'),
            type: "success",
            duration: 3000,
          });
          dispatch({ type: 'LOGOUT' });
        };
      })
      .catch(error => {
        if (error.response && error.response.status == '403') {
          showMessage({
            message: "Could not update the Employee Profile",
            type: "danger",
            duration: 3000,
          });
        } else {
          console.log(error.toJSON());
        };
      });
  };

  const outputErrorMessage = (message) => {
    showMessage({
      message: message,
      type: "danger",
      duration: 3000,
    });
  }

  const validateNewPassword = () => {
    if (newPasswordRef.current === "") {
      setNewPassowrdError(t('common:errRequired'));
      return false;
    } else {
      setNewPassowrdError("");
      return true;
    };
  };

  const validateCurrentPassword = () => {
    if (currentPasswordRef.current === "") {
      setCurrentPasswordError(t('common:errRequired'));
      return false;
    } else {
      setCurrentPasswordError("");
      return true;
    };
  };

  const validateConfirmPassword = () => {
    if (confirmPasswordRef.current === "") {
      setConfirmPasswordError(t('common:errRequired'));
      return false;
    } else if (confirmPasswordRef.current !== newPasswordRef.current) {
      setConfirmPasswordError(t('common:errMismatch'));
      return false;
    }
    else {
      setConfirmPasswordError("");
      return true;
    };
  };

  return (
    <ScrollView
      style={styles.areaview}
      showsVerticalScrollIndicator={false}
      contentContainerStyle={{ flexGrow: 1, justifyContent: 'center', alignItems: 'center' }}>
      <View style={styles.root}>
        {colorScheme === 'light' ?
          <Light /> :
          <Dark />
        }

        <CustomInput
          placeholder={t('common:currPassword')}
          value={currentPassword}
          setValue={setCurrentPassword}
          type="LOGIN"
          autoCapitalize='none'
          secureTextEntry
        />
        <CustomText style={styles.error}>{currentPasswordError}</CustomText>

        <CustomInput
          placeholder={t('common:newPassword')}
          value={newPassword}
          setValue={setNewPassowrd}
          type="LOGIN"
          autoCapitalize='none'
          secureTextEntry
        />
        <CustomText style={styles.error}>{newPasswordError}</CustomText>
        <CustomInput
          placeholder={t('common:verifyNewPassword')}
          value={confirmPassowrd}
          setValue={setConfirmPassword}
          type="LOGIN"
          autoCapitalize='none'
          // No need to send property it default to true anw  ({true})
          secureTextEntry
        />
        <CustomText style={styles.error}>{confirmPassowrdError}</CustomText>
        <CustomButton
          text={t('common:changePassword')}
          // disable while processing one request 
          disabled={isLoading}
          onPress={onSavePassword}
        />

        <CustomText style={styles.copyright}>Oasys by Deloitte</CustomText>

      </View>
    </ScrollView>
  );
};

const makeStyles = (colors, colorScheme) => StyleSheet.create({
  areaview: {
    backgroundColor: '00F0F8FF'
  },
  root: {
    flex: 1,
    alignItems: "center",
    justifyContent: 'center',
    width: Platform.OS === 'web' ? (window.width < 500 ? window.width : window.width * 0.2) : window.width * 0.9,
    padding: 20,
    backgroundColor: colors.background,
  },
  title_hidden: {
    paddingTop: '10%',
    paddingBottom: '10%',
  },
  title: {
    fontSize: 24,
    paddingTop: '20%',
    paddingBottom: '10%',
    fontWeight: 'bold',
    color: colors.text_input,
    margin: 10,
  },
  tinyLogo: {
    width: 152,
    height: 172,
  },
  error: {
    color: colors.validation_error,
    alignSelf: 'center',
    fontSize: 14,
  },
  copyright: {
    marginVertical: '10%',
    paddingBottom: '12%',
    fontSize: 13,
    color: '#6e6e6e',
  }
});

export default UpdatePasswordScreen;